import React, { useContext, useEffect, useRef, useState } from "react";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { mediaProfileUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import ListRow from "../../profiles/ListRow";
import MediaOutletReporterRowSkeleton from "./media_outlet_reporter_row_skeleton";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";
import { MediaOutletContext } from "../data/media_outlet_context_management";

const PER_PAGE = 12;
const FIRST_PAGE_PLACEHOLDERS = 6;
const NEXT_PAGE_PLACEHOLDERS = 3;

// The divider belongs to the list, not the row, and there is none after the last item.
const rowClasses = (index, count) => `py-2${index < count - 1 ? " border-bottom" : ""}`;

// Reduces one raw page of media_jobs to the first job row per profile. The page is ordered by
// media_profile_id, so a profile's rows are adjacent; keeping the first is what makes several
// concurrent jobs at the same outlet render as one reporter.
function firstJobPerProfile(jobRows) {
  const jobByProfileId = new Map();
  jobRows.forEach((row) => {
    if (row.media_profile_id != null && !jobByProfileId.has(row.media_profile_id)) {
      jobByProfileId.set(row.media_profile_id, row);
    }
  });
  return jobByProfileId;
}

// The reporters currently working at an outlet, as a self-contained section card: it owns its
// <SectionCard> so it can collapse away entirely when there is nobody to show. Viewers never get an
// empty card; an admin keeps it with a one-line empty state, so the outlet page reads the same
// whether or not the roster is populated.
//
// The media_jobs view carries no avatar, so a page of the roster costs two reads: the jobs page, then
// the profiles behind it. Both are RLS-scoped views, so a reporter who hides their profile simply
// doesn't come back from the second read and never reaches the page.
function MediaOutletReporters() {
  const { mediaOutletId, mediaOutlet, viewerContext } = useContext(MediaOutletContext);
  const editable = !!viewerContext?.editable;

  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [reporters, setReporters] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // The provider hands out a fresh `mediaOutlet` object on every authoritative read, so its identity --
  // not the outlet id alone -- is this section's refresh signal. It restarts pagination from an empty
  // list, so the rows on screen can never be a mix of pages fetched before and after the refresh, and
  // it is a dependency of the fetch below, so a section already sitting on page 1 refetches too.
  // Taking the new signal during the render that first sees it, rather than in an effect, keeps a
  // refresh to a single request. Dropping `hasMore` matters as much as the rows: it takes the old
  // scroll trigger out of the page, so a trigger sitting in the viewport cannot advance pagination off
  // the pre-refresh list while page 1 is still in flight.
  const [fetchContext, setFetchContext] = useState({ mediaOutletId, mediaOutlet });
  if (fetchContext.mediaOutletId !== mediaOutletId || fetchContext.mediaOutlet !== mediaOutlet) {
    setFetchContext({ mediaOutletId, mediaOutlet });
    setReporters([]);
    setHasMore(false);
    setPage(1);
    // Straight to the loading skeleton: without this the commit that empties the list would paint one
    // frame of the empty state (or collapse the card entirely for a viewer) before the fetch effect
    // gets to run.
    setIsLoading(true);
  }

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  // A page costs two reads, so it can be in flight for a while, and the fetch below is keyed on
  // `page`: advancing the page mid-fetch abandons the reads already running and appends the page
  // after them onto the list, leaving a permanent hole where the abandoned page's reporters belonged.
  // So the page never moves while a fetch is in flight. The trigger that arrived is not thrown away
  // either -- it is left unconsumed and applied once the page settles, so a viewer sitting on the
  // trigger through a slow fetch still gets the next page, exactly once and in order.
  const [pageFetchInFlight, setPageFetchInFlight] = useState(false);

  // Effect to track infinite scrolling intersection observer event, which increments page. Each
  // trigger is consumed exactly once, and only while there is more to fetch: the observer can still
  // deliver a queued intersection for a trigger node a refresh just unmounted, and paging forward off
  // that would append later pages onto a list that is being refetched from page 1.
  const consumedTriggerRef = useRef(0);
  useEffect(() => {
    if (pageFetchInFlight) return;
    if (lastTriggeredAt <= consumedTriggerRef.current) return;

    consumedTriggerRef.current = lastTriggeredAt;
    if (hasMore) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt, hasMore, pageFetchInFlight]);

  useEffect(() => {
    let componentIsMounted = true;
    if (mediaOutletId) setPageFetchInFlight(true);

    const fetchReporters = async () => {
      try {
        if (mediaOutletId) {
          if (page === 1) setIsLoading(true);
          qahoy.track("MediaOutlets#getReporters");

          const params = { page, per_page: PER_PAGE };
          const response = await qapi.mediaJobs.getAllCurrentByMediaOutlet(mediaOutletId, params);
          // Both reads RESOLVE a non-2xx as `{ error }` rather than rejecting. Left alone, a failed
          // read would fall straight through to the settle below and render as an outlet that has no
          // reporters -- an outage must never be presented as valid, empty data.
          if (response.error) throw new Error(JSON.stringify(response.error));
          const jobByProfileId = firstJobPerProfile(Array.isArray(response.data) ? response.data : []);
          const profileIds = [...jobByProfileId.keys()];

          const hydration = profileIds.length ? await qapi.mediaProfiles.getAllById(profileIds) : { data: [] };
          if (hydration.error) throw new Error(JSON.stringify(hydration.error));
          const hydrationRows = Array.isArray(hydration.data) ? hydration.data : [];
          const profileById = new Map(hydrationRows.map((profile) => [profile.profile_id, profile]));

          // The hydration read answers in whatever order it likes, and omits a profile this viewer
          // isn't entitled to, so the page is rebuilt from the job order and mapped back by id --
          // never zipped positionally.
          const pageReporters = profileIds
            .filter((profileId) => profileById.has(profileId))
            .map((profileId) => ({
              id: profileId,
              name: profileById.get(profileId).name,
              profile_image_url: profileById.get(profileId).profile_image_url,
              slug: profileById.get(profileId).slug,
              migrate_id: profileById.get(profileId).migrate_id,
              title: jobByProfileId.get(profileId).title,
            }));

          if (componentIsMounted) {
            // Page 1 replaces instead of appending: it is also what a restart re-fetches, and
            // appending there would stack a second copy of the first page onto the rows on screen.
            // Later pages dedupe against every profile already shown, because one reporter's jobs can
            // straddle a page boundary.
            setReporters((prevRecords) => {
              if (page === 1) return pageReporters;
              const seenProfileIds = new Set(prevRecords.map((reporter) => reporter.id));
              return [...prevRecords, ...pageReporters.filter((reporter) => !seenProfileIds.has(reporter.id))];
            });
            // Pagination advances on RAW job rows remaining, not on rows gained: a page whose every
            // profile was already shown (or is hidden from this viewer) adds nothing, and stopping
            // there would strand the rest of the roster.
            setHasMore(response.remainingRows > 0);
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (componentIsMounted) setAsyncError(error);
      } finally {
        // Only the run that is still current may lower the fence: a superseded run settling late must
        // not release a held trigger onto the page its successor is still fetching.
        if (componentIsMounted) setPageFetchInFlight(false);
      }
    };
    fetchReporters();

    // Cleanup function to avoid the "Can't perform a React state update on an unmounted component"/memory leak problem
    return () => {
      componentIsMounted = false;
    };
  }, [mediaOutletId, page, fetchContext]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  // "Nothing to show" only once pagination has run out: a first page that yielded no visible reporter
  // but has rows remaining is still mid-roster, and collapsing there would unmount the scroll trigger
  // and strand the pages behind it.
  const isEmpty = !isLoading && !hasMore && reporters.length === 0;
  if (isEmpty && !editable) return null;

  return (
    // No edit pen: the roster is derived from the reporters' own media jobs, which they maintain from
    // their profiles — there is nothing here for an outlet curator to edit.
    <SectionCard id="media-outlet-section-reporters" title="Associated Media Users">
      {isEmpty ? (
        <div className="text-muted">No associated media users added yet.</div>
      ) : (
        <>
          <ul className="list-unstyled mb-0">
            {isLoading
              ? Array.from({ length: FIRST_PAGE_PLACEHOLDERS }, (_unused, index) => (
                  <li key={`placeholder-${index}`} className={rowClasses(index, FIRST_PAGE_PLACEHOLDERS)}>
                    <MediaOutletReporterRowSkeleton />
                  </li>
                ))
              : reporters.map((reporter, index) => (
                  <li key={reporter.id} className={rowClasses(index, reporters.length)}>
                    {/* No company in the subtitle: every row on this page works at THIS outlet, and the
                        title comes from the media job here rather than the profile's own headline. */}
                    <ListRow
                      imageUrl={reporter.profile_image_url}
                      href={mediaProfileUrl({ slug: reporter.slug, migrate_id: reporter.migrate_id })}
                      title={reporter.name}
                      subtitleTitle={reporter.title}
                    />
                  </li>
                ))}
          </ul>
          {hasMore && (
            // The trigger's placeholders are a list of their own, not a continuation of the roster
            // above -- an <li> outside a <ul> is invalid and browsers reparent it unpredictably.
            <div ref={scrollTriggerRef}>
              <ul className="list-unstyled mb-0">
                {Array.from({ length: NEXT_PAGE_PLACEHOLDERS }, (_unused, index) => (
                  <li key={`next-placeholder-${index}`} className={rowClasses(index, NEXT_PAGE_PLACEHOLDERS)}>
                    <MediaOutletReporterRowSkeleton />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

export default MediaOutletReporters;
